/**
 * The site-wide banner: one notice, above the masthead, on every page.
 *
 * The whole of turning one on is the object below. `SITE_BANNER` is a banner
 * or it is `null`, and `null` is the ordinary state of the site — nothing is
 * rendered, nothing is stored, and the page is what it was before this file
 * existed. Writing a banner is filling the object in; taking it down is
 * setting it back to `null`.
 *
 * Rendered by layouts/Base.astro into the HTML the server sends, not built
 * here, for the reason the masthead is not built here either: the blog's
 * pages never run the app, and a notice that waited for JavaScript would be
 * missing from most first visits. This module owns the content and the
 * dismissal; the layout owns the markup.
 *
 * Dismissal is per-browser and by `id`. A player who has closed a banner does
 * not see it again — until the `id` changes, which is what makes a new notice
 * a new banner rather than a quiet edit of one somebody has already dismissed.
 * Change the words, change the id.
 *
 * Nothing here is account state. It is a preference about this browser, like
 * the theme, so it lives in localStorage and a signed-out reader has the same
 * say in it as a player.
 */

import { SURVEY_LABEL, SURVEY_URL } from "./survey";

/**
 * A notice.
 *
 * `text` is the sentence. `href` and `linkText` are the one thing to do about
 * it, and they travel together: a banner has a link or it has none.
 */
export interface SiteBanner {
  /** Bump this to bring a dismissed banner back. Never reuse one. */
  readonly id: string;
  readonly text: string;
  readonly href?: string;
  readonly linkText?: string;
}

/**
 * Display a site banner.
 */
export const SITE_BANNER: SiteBanner | null = {
  id: "alpha-survey-2026-08",
  text: "lance.blue is in alpha! Please fill out the survey:",
  href: SURVEY_URL,
  linkText: SURVEY_LABEL,
};

/**
 * Where a dismissal is remembered. One key, holding the id that was closed.
 *
 * Exported because two things read it and they run at different times: the
 * pre-paint check in Base.astro, which removes a banner this browser has
 * already closed before it can be seen, and mountBanner below, which writes
 * it. A dismissed banner that flashed on every page load would be its own
 * kind of nuisance, so the check cannot wait for the module script.
 */
export const BANNER_KEY = "lb.banner-dismissed";

/**
 * Wire the close button, for the banner the layout has already rendered.
 *
 * Called from Base.astro's own script, beside the theme and the account, so
 * it runs on the blog's pages too. It finds nothing and does nothing on a
 * page with no banner on it, which is every page when `SITE_BANNER` is null
 * and every page a reader has already dismissed it on.
 */
export function mountBanner(): void {
  const banner = document.getElementById("site-banner");
  const close = banner?.querySelector<HTMLButtonElement>(".site-banner-close");
  if (!banner || !close) return;

  close.addEventListener("click", () => {
    banner.remove();
    try {
      localStorage.setItem(BANNER_KEY, banner.dataset.bannerId ?? "");
    } catch {
      /* blocked storage: it comes back on the next page, and that is all */
    }
  });
}
